import i18n from "i18next";
import { RcFile } from "antd/lib/upload";
import { ImageRules } from "@type/core/image/image.types";
import { toastError } from "@utils/helpers/toast-helper";

async function loadImageAndCheckWidthHeight(
  imageUrl: string,
  rules: ImageRules,
) {
  const img = new Image();
  const imageLoadPromise = new Promise((resolve, reject) => {
    img.onload = () => {
      if (
        (rules.width && img.width > rules.width) ||
        (rules.height && img.height > rules.height)
      ) {
        toastError(i18n.t("global.img.error_dimension"));
        reject();
      }
      resolve({ width: img.width, height: img.height });
    };
    img.src = imageUrl;
  });

  await imageLoadPromise;
}

export const beforeUpload = (
  file: RcFile,
  rules?: ImageRules,
  sizeErrorI18nKey?: string,
):
  | void
  | boolean
  | string
  | Blob
  | File
  | Promise<void | boolean | string | Blob | File> => {
  const isAllowedMimeType = ["image/jpeg", "image/png", "image/webp"].includes(
    file.type,
  );
  if (!isAllowedMimeType) {
    toastError(i18n.t("common.img.error_extension"));
  }
  let maxWeight;
  if (rules && rules.size) {
    maxWeight = file.size < rules.size;
    if (!maxWeight) {
      toastError(
        i18n.t(sizeErrorI18nKey ?? "global.img.error_size", {
          size: `${rules.size} octets`,
        }),
      );
    }
  } else {
    maxWeight = file.size / 1024 / 1024 < 2;
    if (!maxWeight) {
      toastError(
        i18n.t(sizeErrorI18nKey ?? "common.img.error_size", { size: "2 MB" }),
      );
    }
  }
  if (rules && isAllowedMimeType && maxWeight) {
    return loadImageAndCheckWidthHeight(
      window.URL.createObjectURL(file),
      rules,
    );
  } else {
    return isAllowedMimeType && maxWeight;
  }
};

export const svgElementToPngFile = (
  svgElement: SVGSVGElement,
  fileName: string,
): Promise<File> => {
  return new Promise((resolve, reject) => {
    // Serialize SVG element to string
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
    const url = URL.createObjectURL(svgBlob);

    // Create an Image to render the SVG
    const img = new Image();
    img.onload = () => {
      // Setup canvas
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject("error.svgElementToPngFile.canvasContext");
        return;
      }

      // Draw image onto canvas
      ctx.drawImage(img, 0, 0);

      // Convert canvas to PNG blob
      canvas.toBlob((blob) => {
        if (!blob) {
          reject("error.svgElementToPngFile.canvasToBlobConversion");
          return;
        }

        // Create a file from the blob
        const pngFile = new File([blob], fileName, { type: "image/png" });
        resolve(pngFile);
      }, "image/png");
    };

    img.onerror = () => {
      reject("error.svgElementToPngFile.imageLoadImpossible");
    };

    // Load the image
    img.src = url;
  });
};

// Source: https://stackoverflow.com/questions/73485602/calculate-the-size-of-a-base64-image-in-kb-mb
export const computeBase64ImageSizeInBytes = (base64Data: string): number => {
  const rawImage = base64Data.substring(base64Data.indexOf(",") + 1);
  const sizeInBits = rawImage.length * 6; // 2^6 = 64

  return sizeInBits / 8;
};
